import { Figure, svgType } from './Figure'

/**
 * One transaction, two legs, summing to zero.
 *
 * The worked example is a card purchase because it is the case where people's
 * intuition is wrong: buying groceries on a Visa does not move money out of
 * any account you hold, it increases what you owe. Both legs are drawn with
 * their real signs from migration 023 (positive = value into the account).
 */
export function DoubleEntryDiagram() {
  const rule = 'var(--border)'

  return (
    <Figure
      title="A $200 grocery purchase on a Visa, recorded as two ledger lines: Expense Groceries plus 200.00 and Visa minus 200.00, summing to zero."
      caption="Both lines belong to one journal entry. Postgres checks the sum at COMMIT, not per row, so the two legs can be written one at a time."
    >
      <svg viewBox="0 0 720 276" xmlns="http://www.w3.org/2000/svg">
        {/* Entry header */}
        <text x="0" y="12" {...svgType.faint}>
          Journal entry &middot; 14 Aug &middot; &ldquo;Groceries&rdquo; &middot; source: plaid
        </text>
        <line x1="0" y1="26" x2="720" y2="26" stroke={rule} strokeWidth="1" />

        {/* ── Leg 1: the expense receives value ── */}
        <text x="0" y="62" {...svgType.label}>Expense: Groceries</text>
        <text x="0" y="82" {...svgType.faint}>system account &middot; where spending lands</text>
        <text x="330" y="62" textAnchor="end" {...svgType.num} fill="var(--inflow)">
          +200.00
        </text>
        <text x="330" y="82" textAnchor="end" {...svgType.faint}>debit</text>
        <line x1="0" y1="100" x2="330" y2="100" stroke={rule} strokeWidth="1" />

        {/* ── Leg 2: the card gives it up ── */}
        <text x="0" y="136" {...svgType.label}>Visa &middot;&middot;&middot;4021</text>
        <text x="0" y="156" {...svgType.faint}>Debt &middot; balance becomes more negative</text>
        <text x="330" y="136" textAnchor="end" {...svgType.num} fill="var(--outflow)">
          &minus;200.00
        </text>
        <text x="330" y="156" textAnchor="end" {...svgType.faint}>credit</text>
        <line x1="0" y1="174" x2="330" y2="174" stroke={rule} strokeWidth="1" />

        {/* Sum */}
        <text x="0" y="200" {...svgType.faint}>sum(amount_base)</text>
        <text x="330" y="200" textAnchor="end" {...svgType.num} fill="var(--text)">
          0.00
        </text>

        {/* ── The arrow: value moving from the card to the expense ── */}
        <g stroke="var(--text-faint)" fill="none" strokeWidth="1.25">
          {/* Up the right-hand side, from leg 2 to leg 1. */}
          <path d="M340 143 L436 143 Q446 143 446 133 L446 79 Q446 69 436 69 L345 69" />
        </g>
        <path d="M336 69 L345 64.5 L345 73.5 Z" fill="var(--text-faint)" />
        <text x="460" y="98" {...svgType.faint}>value moves this way</text>
        <text x="460" y="114" {...svgType.faint}>the card gives it up,</text>
        <text x="460" y="130" {...svgType.faint}>the expense receives it</text>

        {/* ── The constraint ── */}
        <line x1="0" y1="228" x2="720" y2="228" stroke={rule} strokeWidth="1" />
        <text x="0" y="248" {...svgType.numSmall} fill="var(--text-faint)">
          CONSTRAINT TRIGGER ledger_lines_balance_check &mdash; DEFERRABLE INITIALLY DEFERRED
        </text>
        <text x="0" y="264" {...svgType.faint}>
          Fewer than two legs, or a sum that is not exactly zero, aborts the COMMIT.
        </text>
      </svg>
    </Figure>
  )
}
